/**
 * Screenshot — capture the live Phaser canvas to a Blob, data URL, or
 * downloaded file.
 *
 * Why not html2canvas: the SDK draws everything in Phaser (WebGL or
 * Canvas), and html2canvas struggles with `<canvas>` elements (it
 * effectively snapshots the *page*, not the *canvas pixels*). Phaser's
 * built-in `game.renderer.snapshot()` reads the canvas pixels directly,
 * which is exactly what we want.
 *
 * Usage from a game scene:
 *
 *   const blob = await this.sdk.screenshot.capture();
 *   // or
 *   await this.sdk.screenshot.download('balloon-pop-menu.png');
 *
 * Usage from automation (Playwright):
 *
 *   await page.evaluate(() => window.__platformSdkTest.captureScreen({ format: 'image/png' }));
 *
 * `download()` is a no-op when `document` is unavailable (e.g. Node tests).
 */
export class Screenshot {
  /**
   * @param {object} opts
   * @param {Phaser.Game} opts.game
   */
  constructor(opts) {
    this.game = opts.game;
  }

  /**
   * Capture the current frame.
   *
   * @param {object} [opts]
   * @param {string}  [opts.format='image/png']
   * @param {number}  [opts.quality=0.92]    only honored for image/jpeg
   * @param {boolean} [opts.asBlob=true]     false → returns a base64 data URL
   * @returns {Promise<Blob|string>}
   */
  async capture(opts = {}) {
    const format = opts.format || 'image/png';
    const quality = opts.quality == null ? 0.92 : opts.quality;
    const asBlob = opts.asBlob !== false;

    if (!this.game || !this.game.renderer || typeof this.game.renderer.snapshot !== 'function') {
      throw new Error('Screenshot.capture: no Phaser renderer available');
    }

    return new Promise((resolve, reject) => {
      try {
        this.game.renderer.snapshot((image) => {
          // Phaser hands us an HTMLImageElement; copy its pixels into an
          // offscreen canvas so we can re-encode at the desired format.
          if (typeof document === 'undefined') {
            // No DOM (e.g. tests). Just return the image src directly.
            return resolve(asBlob ? null : (image && image.src) || '');
          }
          const w = (image && (image.naturalWidth || image.width)) || 0;
          const h = (image && (image.naturalHeight || image.height)) || 0;
          const c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          const ctx = c.getContext('2d');
          if (image) ctx.drawImage(image, 0, 0);

          if (asBlob) {
            if (typeof c.toBlob === 'function') {
              c.toBlob((b) => resolve(b), format, quality);
            } else {
              // Older browsers without toBlob — synthesize from data URL.
              const dataUrl = c.toDataURL(format, quality);
              resolve(dataUrlToBlob(dataUrl));
            }
          } else {
            resolve(c.toDataURL(format, quality));
          }
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Capture and trigger a browser download with the given filename.
   * No-op outside browsers.
   *
   * @param {string} filename
   * @param {object} [opts] forwarded to capture()
   */
  async download(filename, opts) {
    if (typeof document === 'undefined' || typeof URL === 'undefined') return;
    const blob = await this.capture(Object.assign({ asBlob: true }, opts || {}));
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'screenshot.png';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
    }, 1000);
  }

  /**
   * Convenience: capture and return a base64 string (no `data:` prefix),
   * suitable for shipping over a Playwright `page.evaluate()` call where
   * Blob serialization is awkward.
   */
  async captureBase64(opts) {
    const dataUrl = await this.capture(Object.assign({}, opts || {}, { asBlob: false }));
    if (typeof dataUrl !== 'string') return '';
    const i = dataUrl.indexOf(',');
    return i === -1 ? dataUrl : dataUrl.slice(i + 1);
  }
}

/** Tiny helper for browsers without `canvas.toBlob`. */
function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const mime = (parts[0].match(/:(.*?);/) || [])[1] || 'image/png';
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new Blob([u8], { type: mime });
}
